import { createContext, useContext, useState } from 'react';
import { Controller } from 'react-spring';
import { Signal } from 'signals';

export enum AnimateState {
    Default,

    Search,
    SearchToUsers,
    SearchToCourts,
    SearchToSearchCalendarProgress,
    SearchToSearchCalendar,
    SearchToSearchFilter,

    SearchCalendar,
    SearchCalendarToSearchProgress,
    SearchCalendarToSearch,

    SearchFilter,
    SearchFilterToSearch,

    Courts,
    CourtsToSearch,
    CourtsToUsers,
    CourtsToCourts,
    CourtsToCourtsCalendarProgress,
    CourtsToCourtsCalendar,
    CourtsToCourtsFilter,

    CourtsCalendar,
    CourtsCalendarToCourtsProgress,
    CourtsCalendarToCourts,

    CourtsFilter,
    CourtsFilterToCourts,

    Users,
    UsersToSearch,
    UsersToCourts,
    UsersToCourtsBack,
    UsersToUsers,
    UsersToUsersBack,
    UsersToUsersCalendarProgress,
    UsersToUsersCalendar,
    UsersToUsersFilter,

    UsersCalendar,
    UsersCalendarToUsersProgress,
    UsersCalendarToUsers,

    UsersFilter,
    UsersFilterToUsers,
}

export const AnimateContext = createContext({
    state: AnimateState.Default,
    scale: 0,

    controller: new Controller()
        .update({
            alpha: 0,

            config: {
                tension: 210,
                friction: 0,
                mass: 1.5,
                clamp: true,
            },

            immediate: false,
        })
        .start(),
});

export default function useAnimate() {
    const animate = useContext(AnimateContext);

    // const [, setRender] = useState(0);

    return {
        getController() {
            return animate.controller;
        },

        getState() {
            return animate.state;
        },
        setState(state: AnimateState) {
            animate.state = state;

            // setRender(tick => tick + 1);
        },

        getScale() {
            return animate.scale;
        },
        setScale(scale: number) {
            animate.scale = scale;

            // setRender(tick => tick + 1);
        },

        getAlpha() {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return animate.controller.getValues().alpha.value as number;
        },
        setAlpha(alpha = 0, immediate = false, onRest?: () => void) {
            let called = false;

            animate.controller
                .stop()
                .update({ alpha, immediate })
                .start(() => {
                    if (immediate || !onRest || called) {
                        return;
                    }

                    const currentAlpha = this.getAlpha();

                    if (currentAlpha === 0 || currentAlpha === 1) {
                        called = true;
                        onRest();
                    }
                });

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            animate.controller.getValues().alpha.flush();
        },

        /* */

        interpolate: <T>(onUpdate: (alpha: number) => Record<number, T>) => {
            const values = animate.controller.getValues<{ alpha: number }>();
            return values.alpha.interpolate((alpha) => {
                const states = onUpdate(alpha);
                const value = states[animate.state];
                return value != null ? value : states[AnimateState.Default];
            });
        },

        to: <T>(map: { states: AnimateState[], update: (alpha: number) => T }[]) => {
            const values = animate.controller.getValues<{ alpha: number }>();
            return values.alpha.interpolate((alpha) => {
                const fallback = map.find(v => v.states.includes(AnimateState.Default));
                const state = map.find(v => v.states.includes(animate.state));

                let value;
                if (!state) {
                    if (fallback) {
                        value = fallback.update(alpha);
                    }
                } else {
                    value = state.update(alpha);
                }

                return value != null ? value : undefined;
            });
        },
    };
}
